import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { MessageClass } from '@cotera/sdk/core';
import { useTenantedClient } from '@cotera/client/app/stores/org';

/**
 * Resolves the display names of the *other* people in a (potentially shared,
 * org-visible) chat so their messages can be attributed instead of rendering as
 * "Unknown user".
 *
 * Names come from the `listParticipants` endpoint, which derives participants
 * from the distinct authors of persisted messages. The query key intentionally
 * includes the sorted set of foreign actor ids: when a new participant's message
 * arrives over the realtime broadcast, the set grows, the key changes, and a
 * fresh fetch runs — resolving the new name without a page refresh. (Keying only
 * on `conversationId` cached the first result forever, so newly-joined authors
 * stayed "Unknown user" until a hard refresh; the global `refetchOnMount: false`
 * default made that stale entry especially sticky.) `keepPreviousData` keeps
 * already-resolved names visible while that refetch is in flight.
 */
export const useParticipantNames = ({
  conversationId,
  currentUserId,
  messages,
}: {
  conversationId?: string;
  currentUserId?: string;
  messages: MessageClass[];
}): Map<string, string> => {
  const tenantedClient = useTenantedClient();

  // Distinct actor ids for messages authored by someone other than the current
  // user. Sorted so the memoized array is stable by value (react-query hashes
  // the query key structurally, so ordinary message activity that introduces no
  // new author does not trigger a needless refetch).
  const foreignActorIds = useMemo(() => {
    if (
      conversationId === undefined ||
      conversationId.length === 0 ||
      currentUserId === undefined
    ) {
      return [];
    }
    const ids = new Set<string>();
    for (const message of messages) {
      if (!message.isUserMessage()) {
        continue;
      }
      const raw = message.raw() as {
        metadata?: { actorId?: unknown } | null;
      };
      const actorId = raw.metadata?.actorId;
      if (typeof actorId === 'string' && actorId !== currentUserId) {
        ids.add(actorId);
      }
    }
    return [...ids].sort();
  }, [conversationId, currentUserId, messages]);

  const shouldResolveParticipants = foreignActorIds.length > 0;

  const participantsQuery = useQuery({
    queryKey: ['chat-participants', conversationId, foreignActorIds],
    queryFn: async () => {
      const result = await tenantedClient.resource.chat.listParticipants({
        conversationId: conversationId!,
      });
      if (result.isErr()) {
        throw new Error('Failed to resolve chat participants');
      }
      return result.value.participants;
    },
    enabled: shouldResolveParticipants,
    // Keep already-resolved names visible while the refetch (triggered by a new
    // participant) is in flight, instead of flashing everyone to "Unknown user".
    placeholderData: keepPreviousData,
  });

  return useMemo(() => {
    const names = new Map<string, string>();
    for (const participant of participantsQuery.data ?? []) {
      if (participant.name !== null && participant.name.trim().length > 0) {
        names.set(participant.id, participant.name);
      }
    }
    return names;
  }, [participantsQuery.data]);
};
